import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Checkbox,
  Button,
  DatePicker,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useMarkAbsent } from '../hooks/useMarkAbsent';

interface MarkAbsentModalProps {
  open: boolean;
  onClose: () => void;
  studentId: number;
  studentName: string;
  date?: string;
  scheduleId?: number;
  onSuccess?: () => void;
}

const MarkAbsentModal: React.FC<MarkAbsentModalProps> = ({
  open,
  onClose,
  studentId,
  studentName,
  date = dayjs().format('YYYY-MM-DD'),
  scheduleId,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [confirmed, setConfirmed] = useState(false);
  const [selectedDate, setSelectedDate] = useState(date);
  const { mutate: markAbsent, isLoading } = useMarkAbsent(studentId);
  const isSelectedDateInPast = dayjs(selectedDate)
    .startOf('day')
    .isBefore(dayjs().startOf('day'));

  useEffect(() => {
    if (open) {
      setSelectedDate(date);
    }
  }, [date, open]);

  const handleClose = () => {
    form.resetFields();
    setConfirmed(false);
    setSelectedDate(date);
    onClose();
  };

  const handleSubmit = async () => {
    if (isSelectedDateInPast) {
      message.error('Cannot mark student as absent for a past date');
      return;
    }

    try {
      const values = await form.validateFields();
      const payload: {
        date: string;
        note: string | null;
        scheduleId?: number;
      } = {
        date: selectedDate,
        note: values.note || null,
      };

      if (typeof scheduleId === 'number' && scheduleId > 0) {
        payload.scheduleId = scheduleId;
      }

      markAbsent(payload, {
        onSuccess: (data: any) => {
          message.success(
            data?.message ||
              `${studentName} marked as absent for ${dayjs(selectedDate).format(
                'MMM DD, YYYY'
              )}`
          );
          handleClose();
          onSuccess?.();
        },
        onError: (error: any) => {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            'Failed to mark student as absent';
          message.error(errorMessage);
        },
      } as any);
    } catch (error) {
      // Validation failed
    }
  };

  return (
    <Modal
      title="Mark Student as Absent"
      open={open}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isLoading}
          disabled={!confirmed || isSelectedDateInPast}
          onClick={handleSubmit}
        >
          Mark Absent
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" style={{ marginTop: '20px' }}>
        <Form.Item>
          <strong>Student:</strong> {studentName}
        </Form.Item>
        <Form.Item label="Date">
          <DatePicker
            style={{ width: '100%' }}
            value={dayjs(selectedDate)}
            disabledDate={(current) =>
              !!current &&
              current.startOf('day').isBefore(dayjs().startOf('day'))
            }
            onChange={(nextDate) => {
              if (nextDate) {
                setSelectedDate(nextDate.format('YYYY-MM-DD'));
              }
            }}
          />
        </Form.Item>

        <Form.Item
          name="note"
          label="Reason for Absence (Optional)"
          rules={[
            {
              max: 1000,
              message: 'Reason must not exceed 1000 characters',
            },
          ]}
        >
          <Input.TextArea
            rows={4}
            placeholder="Enter the reason for absence (e.g., sick, appointment, family matter)"
            maxLength={1000}
            showCount
          />
        </Form.Item>

        <Form.Item>
          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          >
            I confirm that <strong>{studentName}</strong> will not attend on{' '}
            <strong>{dayjs(selectedDate).format('MMM DD, YYYY')}</strong>
          </Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default MarkAbsentModal;
